export type PlayerState =
  | 'idle'
  | 'waiting' // armed, polling for go-live (waitForLive)
  | 'loading'
  | 'playing'
  | 'buffering'
  | 'paused'
  | 'ended'
  | 'error';

export interface PlaybackStats {
  droppedFrames: number;
  /**
   * TOTAL frames this session — the denominator dropped frames need, and it
   * ALREADY INCLUDES the dropped ones (it is
   * `VideoPlaybackQuality.totalVideoFrames`). So the ratio is
   * `droppedFrames / decodedFrames`, with nothing added to the bottom.
   *
   * The name says "decoded" because it is the wire field's name and a
   * contract rename is not worth a migration (plan D10). Read it as "total".
   */
  decodedFrames?: number;
  /** Live-edge latency in seconds (hls.js engine only). */
  latencySeconds?: number;
  /** The live-sync target latency hls.js is converging to, in seconds. Compare
   *  against latencySeconds to see drift. */
  targetLatencySeconds?: number;
  /** Estimated bandwidth in kbps (hls.js engine only). */
  bandwidthKbps?: number;
  /** Height of the current rendition, when known. */
  levelHeight?: number;
  /** Whether the stream is being played in low-latency mode. */
  lowLatency?: boolean;
  /** Whether the current media playlist is live. */
  isLive?: boolean;
  /** Whether playback is at/near the live edge (within the live sync window). */
  atLiveEdge?: boolean;
  /** Cumulative media segment bytes fetched this session (hls.js engine only). */
  bytesLoaded?: number;
  /** Cumulative manifest/playlist bytes fetched this session (hls.js engine only). */
  playlistBytesLoaded?: number;
  /** Cumulative ABR rendition switches this session (hls.js engine only). */
  renditionSwitches?: number;
  /** Bitrate of the current rendition in kbps, when known. */
  renditionBitrateKbps?: number;
  /** Duration of the most recent DRM license acquisition in ms (0 = none yet). */
  licenseTimeMs?: number;

  // ---- Contract v2 (QoE plane). Cumulative where the beacon layer takes
  // deltas; a snapshot otherwise. Engines fill what they can observe.
  /** RFC 6381 codec strings of the current rendition. */
  videoCodec?: string;
  audioCodec?: string;
  /** The offered rendition ladder, ascending heights, and its top bitrate. */
  ladderHeights?: number[];
  ladderTopBitrateKbps?: number;
  /** DRM the engine negotiated for the session. */
  keySystem?: 'widevine' | 'fairplay' | 'none';
  /** Robustness requested/granted, e.g. Widevine L3 (software) on the web. */
  drmSecurityLevel?: string;
  /** The viewer pinned a quality; ABR is not deciding. */
  qualityPinned?: boolean;
  /** Cumulative ABR switch direction split (renditionSwitches = both). */
  upshifts?: number;
  downshifts?: number;
  /** Cumulative media requests completed / failed, and the durations of the
   *  most recent ones (ms, newest last, bounded) for interval percentiles. */
  requestCount?: number;
  requestErrors?: number;
  requestTimesMs?: number[];
  /** Host of the most recent media request — host only, never the path. */
  cdnHost?: string;
}

export const EMPTY_STATS: PlaybackStats = { droppedFrames: 0 };

/**
 * Host of a media URL, without the path. The beacon plane records WHICH CDN
 * served the session — so region and vendor come from the client with no IP
 * anywhere in the chain — never the path, which on a signed slot carries the
 * token.
 *
 * Shared by both engines on purpose: two copies would be two answers to
 * "which CDN was this", and the column is a LowCardinality string where
 * `CDN.example` and `cdn.example` would become two entries for one CDN. The
 * URL parser lowercases for us. `host`, not `hostname`: the port is signal.
 */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

/** Media URLs, playlists included. The playlist matters more than the
 *  segments here — see mediaHostSince. */
const MEDIA_URL = /\.(m3u8|ts|m4s|mp4|cmfv|cmfa)(\?|$)/i;

/**
 * The CDN host for an engine that does its own fetching, read back out of
 * Resource Timing.
 *
 * WebKit's native HLS player exposes NO per-segment network detail to script,
 * which is why the native engine reports no request timings. It does however
 * leave the media load in the resource timeline, so the host is recoverable
 * even though the transfer is not.
 *
 * Measured on iOS 26 (real WebKit, cross-origin stream), 2026-09-17:
 *
 *   master.m3u8   1 entry, initiatorType 'video'
 *   segments      0 entries, over 22 s of confirmed playback
 *
 * So this finds the PLAYLIST, not a segment, and two things follow:
 *
 *   - It works cross-origin with no `Timing-Allow-Origin` anywhere. TAO gates
 *     the TIMING fields, never `name` — the entry arrives with responseStart
 *     and transferSize zeroed and its URL intact, and the URL is all we want.
 *   - The host is fixed at session start. hls.js re-reads it on every
 *     fragment precisely to catch a mid-session CDN failover; this engine
 *     cannot see one. Where a deployment serves playlists and segments from
 *     different hostnames, this would name the playlist's.
 *
 * `since` is a performance.now() mark taken when the engine was built, so a
 * stream played EARLIER on the same page cannot donate its host to this
 * session.
 */
export function mediaHostSince(since: number): string {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return '';
  }
  let best = '';
  let bestStart = -1;
  // getEntriesByType is typed as PerformanceEntry[]; the 'resource' list is
  // PerformanceResourceTiming, which is where initiatorType lives.
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  for (const e of entries) {
    if (e.startTime < since) continue;
    // initiatorType 'video' is what WebKit attributes the native media load
    // to; the extension test carries any engine that attributes differently.
    if (e.initiatorType !== 'video' && e.initiatorType !== 'audio' && !MEDIA_URL.test(e.name)) {
      continue;
    }
    if (e.startTime >= bestStart) {
      const host = hostOf(e.name);
      if (host) {
        best = host;
        bestStart = e.startTime;
      }
    }
  }
  return best;
}

/** A selectable video rendition. */
export interface QualityLevel {
  /** Index into getQualityLevels() — pass to setQuality(). */
  index: number;
  width?: number;
  height?: number;
  bitrate: number;
  codecs?: string;
}

/** A selectable audio rendition. */
export interface AudioTrackInfo {
  id: number;
  name: string;
  lang?: string;
  default: boolean;
}

/** A selectable text (caption/subtitle) track. */
export interface TextTrackInfo {
  id: number;
  name: string;
  lang?: string;
}

/**
 * Frame counts via the standard media quality API (engine-agnostic). Both
 * numbers come from ONE call: a drop count without its denominator says
 * nothing — 300 dropped frames is a broken session at 30fps and a rounding
 * error over an hour.
 *
 * 🔑 `totalFrames`, NOT `decoded`. It is `VideoPlaybackQuality.totalVideoFrames`,
 * which the spec defines as every frame that WOULD have been presented —
 * dropped frames included. So `dropped / totalFrames` is already dropped over
 * total, and the denominator needs no correction.
 *
 * The old name was `decoded`, which reads as "successfully decoded" and
 * therefore as "excluding dropped". That invites a reader to fix a ratio that
 * was never broken, by switching the denominator to `decoded + dropped` and
 * double-counting every dropped frame. The maths was right; only the name was
 * wrong. Pinned by test/frames.test.ts.
 *
 * The WIRE field keeps its name (`decoded_frames_*`): renaming a contract
 * field to fix a comment is not worth a migration.
 */
export function readFrameStats(video: HTMLVideoElement): { dropped: number; totalFrames: number } {
  const q = (
    video as HTMLVideoElement & { getVideoPlaybackQuality?: () => VideoPlaybackQuality }
  ).getVideoPlaybackQuality?.();
  return { dropped: q?.droppedVideoFrames ?? 0, totalFrames: q?.totalVideoFrames ?? 0 };
}

/**
 * Map raw <video> media events to player states. Engine-agnostic: both the
 * hls.js and native engines drive the same state machine through this.
 */
export function attachMediaStateListeners(
  video: HTMLVideoElement,
  onState: (state: PlayerState) => void,
): () => void {
  const handlers: Array<[keyof HTMLMediaElementEventMap, () => void]> = [
    ['playing', () => onState('playing')],
    ['waiting', () => onState('buffering')],
    ['stalled', () => onState('buffering')],
    [
      'pause',
      () => {
        if (!video.ended) onState('paused');
      },
    ],
    ['ended', () => onState('ended')],
  ];
  for (const [ev, fn] of handlers) video.addEventListener(ev, fn);
  return () => {
    for (const [ev, fn] of handlers) video.removeEventListener(ev, fn);
  };
}
